/**
 * ClimaPage — Marcar estado climático de las salidas próximas.
 * Útil para suspender/reagendar por mal tiempo o alertas DIMAR.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box, Typography, Paper, Stack, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, TextField, MenuItem, Alert,
} from '@mui/material';
import CloudIcon from '@mui/icons-material/Cloud';
import EmptyState from '../common/EmptyState';
import useToast from '../common/useToast';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';
function getEmpresaId() { try { return JSON.parse(localStorage.getItem('user') || '{}').company_id || '1'; } catch { return '1'; } }
function authHeaders() { const t = localStorage.getItem('token'); return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) }; }
function fmtFecha(s) { if (!s) return '—'; try { return new Date(s).toLocaleDateString('es-CO', { weekday: 'short', day: 'numeric', month: 'short' }); } catch { return s; } }

const CLIMA_OPTS = [
  { v: 'normal',    label: '☀️ Normal',     color: '#16a34a', bg: '#dcfce7' },
  { v: 'lluvia',    label: '🌧️ Lluvia',     color: '#0ea5e9', bg: '#dbeafe' },
  { v: 'tormenta',  label: '⛈️ Tormenta',   color: '#dc2626', bg: '#fee2e2' },
  { v: 'cancelado', label: '🚫 Cancelado',  color: '#991b1b', bg: '#fecaca' },
];

export default function ClimaPage() {
  const empresaId = getEmpresaId();
  const { refreshCounts } = useOutletContext() || {};
  const { toast, ToastContainer } = useToast();
  const [salidas, setSalidas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [climaSel, setClimaSel] = useState('normal');
  const [nota, setNota] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/salidas?empresa_id=${empresaId}`, { headers: authHeaders() });
      const data = await r.json();
      const list = Array.isArray(data) ? data : (data.salidas || []);
      const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
      setSalidas(list.filter(s => s.fecha && new Date(s.fecha) >= hoy && s.estado !== 'completada')
        .sort((a, b) => new Date(a.fecha) - new Date(b.fecha)));
    } catch (e) { toast.error('Error cargando'); }
    setLoading(false);
  }, [empresaId]); // eslint-disable-line
  useEffect(() => { cargar(); }, [cargar]);

  const abrir = (s) => {
    setDialog(s);
    setClimaSel(s.estado_clima || 'normal');
    setNota(s.nota_clima || '');
  };

  const guardar = async () => {
    if (!dialog) return;
    try {
      const r = await fetch(`${API_BASE}/turismo/salidas/${dialog.id}/clima`, {
        method: 'PATCH', headers: authHeaders(),
        body: JSON.stringify({ estado_clima: climaSel, nota_clima: nota || null }),
      });
      if (!r.ok) throw new Error('Error');
      toast.success('Estado climático actualizado');
      setDialog(null);
      cargar();
      refreshCounts?.();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Estado climático</Typography>
        <Typography sx={{ fontSize: 12, color: '#64748b' }}>
          Marca el clima de cada salida para decidir suspensión, retraso o reprogramación (DIMAR).
        </Typography>
      </Box>

      {salidas.length === 0 ? (
        <EmptyState icon={<CloudIcon sx={{ fontSize: 56, color: '#cbd5e1' }} />}
          title="Sin salidas futuras" message="Cuando programes salidas, aparecerán aquí." />
      ) : (
        <Stack spacing={1}>
          {salidas.map(s => {
            const c = CLIMA_OPTS.find(x => x.v === (s.estado_clima || 'normal')) || CLIMA_OPTS[0];
            return (
              <Paper key={s.id} sx={{ p: 1.5, borderRadius: 2, borderLeft: `4px solid ${c.color}`, cursor: 'pointer' }}
                onClick={() => abrir(s)}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                      {fmtFecha(s.fecha)} · {s.hora_salida?.slice(0, 5)} · {s.paquete_nombre}
                    </Typography>
                    {s.nota_clima && (
                      <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.25 }}>📝 {s.nota_clima}</Typography>
                    )}
                  </Box>
                  <Chip label={c.label} sx={{ bgcolor: c.bg, color: c.color, fontWeight: 700, fontSize: 11 }} />
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Actualizar clima</DialogTitle>
        <DialogContent>
          {dialog && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Alert severity="info" sx={{ fontSize: 12 }}>
                {fmtFecha(dialog.fecha)} · {dialog.paquete_nombre}
              </Alert>
              <TextField select size="small" label="Estado climático" value={climaSel}
                onChange={e => setClimaSel(e.target.value)}>
                {CLIMA_OPTS.map(o => <MenuItem key={o.v} value={o.v}>{o.label}</MenuItem>)}
              </TextField>
              <TextField size="small" multiline minRows={2} label="Nota (opcional)" value={nota}
                onChange={e => setNota(e.target.value)} placeholder="Ej: DIMAR emitió alerta amarilla. Esperar a confirmar." />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disableElevation>Guardar</Button>
        </DialogActions>
      </Dialog>

      <ToastContainer />
    </Box>
  );
}
